import { useEffect, useState } from "react";
import classNames from "classnames";
import { Typography } from "components/atoms/Typography";
import { DropDown } from "components/atoms/DropDown";
import { HorizontalProgressBar } from "../HorizontalProgressBar";
import { useGetPerformanceByTopicQuery } from "services/homeApi";
import { ArrowRight, Spinner } from "components/atoms/Icon";
import { FetchError } from "../FetchErrorMsg";
import "./index.scss";

interface IProps {
  isMobile?: boolean;
  isTab?: boolean;
  theme: "light" | "dark";
}

export const PerformanceByTopic = ({ isMobile, isTab, theme }: IProps) => {
  const [hasData, setHasData] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState<
    null | string | { [key: number]: string }
  >(null);
  const [selectedTopic, setSelectedTopic] = useState<
    null | string | { [key: number]: string }
  >(null);
  const [findCategory, setFindCategory] = useState<any>();
  const [toggleShowMore, setToggleShowMore] = useState(false);
  const { data, isLoading, isError } = useGetPerformanceByTopicQuery();

  const handleToggleView = () => setToggleShowMore((prev) => !prev);

  const getColor = (color: string | null) => {
    switch (color) {
      case "RED":
        return "red";
      case "YELLOW":
        return "yellow";
      case "GREEN":
        return "green";
      case null:
        return "grey";
      default:
        return "grey";
    }
  };

  useEffect(() => {
    setSelectedTopic(null);
    setHasData(false);
  }, [selectedSubject]);

  useEffect(() => {
    if (data && selectedSubject !== null && selectedTopic !== null) {
      setFindCategory(
        data.Subjects[Object.keys(selectedSubject)[0]].Topics[
          Object.keys(selectedTopic)[0]
        ].Categories
      );
      setHasData(true);
    }
  }, [selectedTopic]);

  return (
    <div className={classNames("perform-by-topic-wrapper")}>
      <Typography variant="h7Bold" label="View your performance by topic(s):" />
      <div className={classNames("perform-by-topic-filter-wrapper")}>
        {isLoading ? (
          <Spinner theme={theme}  />
        ) : isError ? (
          <FetchError content="Oops! failed to load resources." />
        ) : (
          <>
            <div className={classNames("perform-by-topic-form-wrapper")}>
              <Typography variant="h8Bold" label="Subject:" />
              <DropDown
                data={
                  data &&
                  data.Subjects.map(
                    (value: any) => value.AssessmentObjective.Name
                  )
                }
                setDropDownSelection={setSelectedSubject}
                defaultSelected={selectedSubject}
                placeholder="Select subject"
              />
              <Typography variant="h8Bold" label="Topic:" />
              <DropDown
                data={
                  data &&
                  data.Subjects[
                    selectedSubject === null
                      ? 0
                      : Object.keys(selectedSubject)[0]
                  ].Topics.map((value: any) => value.AssessmentObjective.Name)
                }
                setDropDownSelection={setSelectedTopic}
                defaultSelected={selectedTopic}
                placeholder="Select topic"
              />
            </div>
            <div className={classNames("perform-by-topic-graph-wrapper")}>
              {hasData ? (
                <>
                  {Object.keys(findCategory).length > 5 && !toggleShowMore
                    ? Object.keys(findCategory)
                        .slice(0, 5)
                        .map((category: any, index: number) => (
                          <div
                            key={index}
                            className={classNames(
                              "perform-by-topic-progress-item"
                            )}
                          >
                            {isMobile ? null : (
                              <div
                                className={classNames(
                                  "perform-by-topic-progress-item-title"
                                )}
                              >
                                <Typography variant="h8Med" label={category} />
                              </div>
                            )}

                            <HorizontalProgressBar
                              complete={findCategory[category].Correct}
                              total={findCategory[category].Total}
                              percentage={findCategory[category].Percentage}
                              isShowProgressCount={false}
                              isShowPercentageOnTop={false}
                              progressColor={getColor(
                                findCategory[category].Total === 0 && findCategory[category].Correct === 0
                                ? null
                                : findCategory[category].Total > 0 && findCategory[category].Correct === 0
                                  ? "RED"
                                  : findCategory[category].Color
                              )}
                              styles={
                                findCategory[category].Correct === 0 ? "" :
                                findCategory[category].Total > 0 && findCategory[category].Correct === 0
                                  ? "perform-by-topic-outer-bar-red"
                                  : ""
                              }
                              label={`${findCategory[category].Percentage}% -  
                          ${findCategory[category].Correct} out of ${findCategory[category].Total} Correct`}
                              isMobile={isMobile}
                              topic={category}
                            />
                          </div>
                        ))
                    : Object.keys(findCategory).map(
                        (category: any, index: number) => (
                          <div
                            key={index}
                            className={classNames(
                              "perform-by-topic-progress-item"
                            )}
                          >
                            {isMobile ? null : (
                              <div
                                className={classNames(
                                  "perform-by-topic-progress-item-title"
                                )}
                              >
                                <Typography variant="h8Med" label={category} />
                              </div>
                            )}

                            <HorizontalProgressBar
                              complete={findCategory[category].Correct}
                              total={findCategory[category].Total}
                              percentage={findCategory[category].Percentage}
                              isShowProgressCount={false}
                              isShowPercentageOnTop={false}
                              progressColor={getColor(
                                findCategory[category].Total === 0 && findCategory[category].Correct === 0
                                ? null
                                : findCategory[category].Total > 0 && findCategory[category].Correct === 0
                                  ? "RED"
                                  : findCategory[category].Color
                              )}
                              styles={
                                findCategory[category].Correct === 0 ? "" :
                                findCategory[category].Total > 0 && findCategory[category].Correct === 0
                                  ? "perform-by-topic-outer-bar-red"
                                  : ""
                              }
                              label={`${findCategory[category].Percentage}% -  
                          ${findCategory[category].Correct} out of ${findCategory[category].Total} Correct`}
                              isMobile={isMobile}
                              topic={category}
                            />
                          </div>
                        )
                      )}
                  {Object.keys(findCategory).length > 5 ? (
                    <div
                      className={classNames(
                        "perform-by-topic-toggle-view-wrapper"
                      )}
                    >
                      <div
                        className={classNames("toggle-show-text-icon-wrapper")}
                        onClick={handleToggleView}
                      >
                        <Typography
                          variant="h9Medium"
                          label={!toggleShowMore ? "Show more" : "Show less"}
                        />
                        <ArrowRight
                          color={theme === "light" ? "black" : "white"}
                        />
                      </div>
                    </div>
                  ) : null}
                </>
              ) : (
                <Typography
                  variant="h8Book"
                  label="Apply a filter to view your performance on this section"
                  styles="perform-by-topic-no-data"
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
